/**
 * Search across the player's own memory: journal entries, sessions, meals and
 * calendar events. Lives at the top of the Brain view, over the memory map.
 * The scoring is the worker's job (worker/search.js); this file is the field,
 * the grouped results, and the jump to whichever view holds the hit.
 */

let ctx = null;
let box = null, field = null, clearBtn = null, out = null;
let timer = null, seq = 0, lastQuery = '';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Same idea as the worker: the words worth highlighting, nothing else.
const STOP = new Set('a an and are as at be but by for from had has have how i in is it its my of on or the to was were what when where who why with you your'.split(' '));
const terms = (q) => {
  const words = String(q || '').toLowerCase().match(/[a-z0-9]{2,}/g) || [];
  return [...new Set(words.filter((w) => !STOP.has(w)))].slice(0, 8);
};

// What to call each view in the toast, so "Opened your nutrition" reads like English.
const SPOT = { journal: 'journal', sessions: 'sessions', nutrition: 'nutrition log', calendar: 'calendar' };

const KINDS = {
  journal: { label: 'Journal', cls: 'k-journal' },
  session: { label: 'Sessions', cls: 'k-session' },
  meal: { label: 'Nutrition', cls: 'k-meal' },
  event: { label: 'Calendar', cls: 'k-event' },
};

/** Escape everything, then wrap the matched words so the eye lands on them. */
function marked(text, ts) {
  const s = String(text ?? '');
  if (!ts.length) return esc(s);
  const re = new RegExp(`(${ts.map(escRe).join('|')})`, 'gi');
  return s.split(re).map((piece, i) => (i % 2 ? `<mark>${esc(piece)}</mark>` : esc(piece))).join('');
}

const when = (at) => {
  const d = new Date(at);
  if (!at || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const hint = 'Search everything you have saved: journal entries, what you and your companion said, session reports, meals and what is on your calendar.';

export function mount(c) {
  ctx = c;
  box = document.getElementById('searchRoot');
  if (!box) return;
  box.innerHTML = `
    <div class="card srch">
      <h3>Search your memory</h3>
      <div class="srch-bar">
        <input id="srchIn" type="text" placeholder="elbow, chicken and rice, Tuesday practice" autocomplete="off" spellcheck="false">
        <button class="btn" id="srchClear" type="button" hidden>Clear</button>
      </div>
      <div class="srch-out" id="srchOut"><p class="srch-hint">${esc(hint)}</p></div>
    </div>`;
  field = document.getElementById('srchIn');
  clearBtn = document.getElementById('srchClear');
  out = document.getElementById('srchOut');

  field.addEventListener('input', () => {
    clearBtn.hidden = !field.value;
    clearTimeout(timer);
    timer = setTimeout(run, 200);
  });
  field.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { clearTimeout(timer); run(); }
    if (e.key === 'Escape') reset();
  });
  clearBtn.addEventListener('click', reset);
}

/** Safe to call any time: repaints whatever the field holds right now. */
export async function render() {
  if (!box) return;
  if (!field.value.trim()) { paintHint(); return; }
  await run();
}

function reset() {
  if (!field) return;
  field.value = ''; clearBtn.hidden = true; lastQuery = '';
  clearTimeout(timer); seq++;
  paintHint();
  field.focus();
}

function paintHint() {
  // Clearing the field also clears the guard, so retyping the same words searches again.
  lastQuery = '';
  if (out) out.innerHTML = `<p class="srch-hint">${esc(hint)}</p>`;
}

async function run() {
  if (!box) return;
  const q = field.value.trim();
  if (q.length < 2) { paintHint(); return; }
  if (q === lastQuery) return;
  lastQuery = q;
  const mine = ++seq;
  out.innerHTML = '<p class="srch-hint">Looking…</p>';
  try {
    const data = await ctx.api(`/api/memory/search?q=${encodeURIComponent(q)}&limit=30`);
    if (mine !== seq) return;
    paint(data.results || [], q);
  } catch (e) {
    if (mine !== seq) return;
    lastQuery = '';  // let them try the same words again
    out.innerHTML = `<p class="srch-hint err">Could not search right now: ${esc(e.message)}</p>`;
  }
}

function paint(results, q) {
  if (!results.length) {
    out.innerHTML = `<p class="srch-hint">Nothing matched "${esc(q)}". Your journal, sessions, meals and events were all searched.</p>`;
    return;
  }
  const ts = terms(q);
  // Groups lead with whichever kind holds the best hit.
  const groups = [];
  for (const r of results) {
    let g = groups.find((x) => x.kind === r.kind);
    if (!g) { g = { kind: r.kind, rows: [] }; groups.push(g); }
    g.rows.push(r);
  }
  out.innerHTML = `<p class="srch-count">${results.length} result${results.length === 1 ? '' : 's'} for "${esc(q)}"</p>`
    + groups.map((g) => {
      const k = KINDS[g.kind] || { label: g.kind, cls: '' };
      return `<div class="srch-group">
        <h4><span class="srch-kind ${k.cls}">${esc(k.label)}</span><span>${g.rows.length}</span></h4>
        ${g.rows.map((r) => `
          <button class="srch-hit" type="button" data-view="${esc(r.view || '')}" data-at="${esc(r.at || '')}">
            <b>${marked(r.title, ts)}</b>
            <time>${esc(when(r.at))}</time>
            <p>${marked(r.text, ts)}</p>
          </button>`).join('')}
      </div>`;
    }).join('');

  out.querySelectorAll('.srch-hit').forEach((b) => {
    b.addEventListener('click', () => {
      const view = b.dataset.view;
      if (!view) return;
      ctx.go(view);
      const day = when(b.dataset.at);
      const spot = SPOT[view] || view;
      ctx.toast(day ? `Opened your ${spot}. The one you picked is from ${day}.` : `Opened your ${spot}.`);
    });
  });
}
